import { useState } from 'react'

const OPERATIONS_EMAIL = 'operations@hivishydrant.com'

const services = [
  {
    title: 'Visibility & Condition Assessment',
    icon: '/assets/icons/HVH_Icon_Assessment.svg',
    body: 'Owner-designated route assessments focused on visibility, exterior condition, identification, access, coating condition and obvious field concerns.'
  },
  {
    title: 'Asset Documentation',
    icon: '/assets/icons/HVH_Icon_Documentation.svg',
    body: 'Asset-level photographs, location or GPS records, identification notes and standardized closeout documentation for clearer maintenance decisions.'
  },
  {
    title: 'Exterior Restoration',
    icon: '/assets/icons/HVH_Icon_Restoration.svg',
    body: 'Owner-authorized exterior cleaning, preparation, coating restoration and approved visibility treatments performed to the asset owner’s specifications.'
  },
  {
    title: 'Identification & Mapping',
    icon: '/assets/icons/HVH_Icon_LocationMapping.svg',
    body: 'Consistent field identification and location records that help teams find, reference and manage hydrant assets more efficiently.'
  },
  {
    title: 'Recurring Maintenance Support',
    icon: '/assets/icons/HVH_Icon_Maintenance.svg',
    body: 'Route-based inspection and maintenance programs designed to keep exterior condition, visibility and documentation from falling behind.'
  },
  {
    title: 'Closeout & Verification',
    icon: '/assets/icons/HVH_Icon_Verify.svg',
    body: 'Before-and-after evidence, completed-scope verification and organized records for municipal, utility and facility stakeholders.'
  }
]

const process = [
  ['01', 'Assess', '/assets/icons/HVH_Icon_Assessment.svg', 'Review asset visibility, exterior condition, identification and access.'],
  ['02', 'Document', '/assets/icons/HVH_Icon_Documentation.svg', 'Capture standardized photos, field notes and location records.'],
  ['03', 'Restore', '/assets/icons/HVH_Icon_Restoration.svg', 'Complete owner-authorized exterior restoration and marking scope.'],
  ['04', 'Verify', '/assets/icons/HVH_Icon_Verify.svg', 'Record completed work with asset-level before-and-after evidence.'],
  ['05', 'Maintain', '/assets/icons/HVH_Icon_Maintenance.svg', 'Return on an agreed route or maintenance cycle when needed.']
]

const trustPoints = [
  'Municipal and utility-oriented field documentation',
  'Owner-approved specifications and markings only',
  'Per-asset and route-based service model',
  'Before-and-after closeout records',
  'Defined phase-one scope with no unsupported technical claims'
]

function AssetIcon({ src, alt = '' }) {
  const [available, setAvailable] = useState(true)
  if (!available) return null
  return <img className="asset-icon" src={src} alt={alt} onError={() => setAvailable(false)} />
}

function BrandLockup({ compact = false, reversed = false }) {
  const desktopLogo = reversed
    ? '/assets/logos/HVH_Primary_White_LiveText.svg'
    : '/assets/logos/HVH_Primary_FullColor_LiveText.svg'

  return (
    <a
      className={`brand-lockup ${compact ? 'brand-lockup--compact' : ''} ${reversed ? 'brand-lockup--reversed' : ''}`}
      href="#top"
      aria-label="Hi-Vis Hydrant Restoration and Repair home"
      style={{ minWidth: 0 }}
    >
      <picture>
        {!reversed && (
          <source
            media="(max-width: 720px)"
            srcSet="/assets/logos/HVH_Primary_FullColor_LiveText.svg"
          />
        )}
        <img
          src={desktopLogo}
          alt="Hi-Vis Hydrant Restoration & Repair"
          width="760"
          height="210"
          style={{
            display: 'block',
            width: 'auto',
            height: compact ? '52px' : '60px',
            maxWidth: 'min(62vw, 320px)',
            objectFit: 'contain'
          }}
        />
      </picture>
    </a>
  )
}

function ArrowIcon() {
  return <span aria-hidden="true">↗</span>
}

function QuoteForm() {
  const [status, setStatus] = useState('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const endpoint = import.meta.env.VITE_QUOTE_FORM_ENDPOINT

  async function handleSubmit(event) {
    event.preventDefault()
    const form = event.currentTarget
    const data = Object.fromEntries(new FormData(form).entries())
    if (!endpoint) {
      setErrorMessage('Form submission is not configured. Please try again later.')
      setStatus('error')
      return
    }

    try {
      setErrorMessage('')
      setStatus('sending')
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, destination: OPERATIONS_EMAIL, source: 'hivishydrant.com' })
      })

      const payload = await response.json().catch(() => ({}))
      if (!response.ok || payload.ok === false) {
        setErrorMessage(payload.error || 'Submission failed — please try again.')
        setStatus('error')
        return
      }

      form.reset()
      setStatus('sent')
    } catch (err) {
      console.error(err)
      setErrorMessage('Unable to submit request. Please try again.')
      setStatus('error')
    }
  }

  return (
    <form className="quote-form" onSubmit={handleSubmit}>
      <div className="form-grid">
        <label>
          <span>Name *</span>
          <input name="name" type="text" autoComplete="name" required />
        </label>
        <label>
          <span>Organization</span>
          <input name="organization" type="text" autoComplete="organization" />
        </label>
        <label>
          <span>Email *</span>
          <input name="email" type="email" autoComplete="email" required />
        </label>
        <label>
          <span>Phone</span>
          <input name="phone" type="tel" autoComplete="tel" />
        </label>
        <label>
          <span>Service Needed *</span>
          <select name="service" defaultValue="" required>
            <option value="" disabled>Select a service</option>
            <option>Visibility & Condition Assessment</option>
            <option>Asset Documentation</option>
            <option>Exterior Restoration</option>
            <option>Identification & Mapping</option>
            <option>Recurring Maintenance Support</option>
            <option>Municipal / Utility Pilot Route</option>
            <option>Other / Not Sure</option>
          </select>
        </label>
        <label>
          <span>Approx. Asset Count</span>
          <input name="assetCount" type="text" inputMode="numeric" placeholder="e.g. 25–50" />
        </label>
        <label className="form-span-2">
          <span>Service Area / Location</span>
          <input name="location" type="text" placeholder="City, parish/county, facility or route" />
        </label>
        <label className="form-span-2">
          <span>Project Details</span>
          <textarea name="details" rows="5" placeholder="Tell us what you need assessed, documented, restored or maintained." />
        </label>
      </div>
      <div className="form-footer">
        <button className="button button--dark" type="submit" disabled={status === 'sending'}>
          {status === 'sending' ? 'Sending…' : 'Request a Quote'} <ArrowIcon />
        </button>
        <p>Requests route to <a href={`mailto:${OPERATIONS_EMAIL}`}>{OPERATIONS_EMAIL}</a>.</p>
      </div>
      {status === 'sent' && <p className="form-status" role="status">Request received. Hi-Vis will follow up using the contact information provided.</p>}
      {status === 'error' && <p className="form-status form-status--error" role="alert">{errorMessage}</p>}
    </form>
  )
}

export default function App() {
  return (
    <div className="site-shell" id="top">
      <div className="utility-bar">
        <div className="container utility-inner">
          <span>HI-VIS HYDRANT RESTORATION &amp; REPAIR</span>
          <a href={`mailto:${OPERATIONS_EMAIL}`}>{OPERATIONS_EMAIL}</a>
        </div>
      </div>

      <header className="site-header">
        <div className="container header-inner">
          <BrandLockup compact />
          <nav className="desktop-nav" aria-label="Primary navigation">
            <a href="#services">Services</a>
            <a href="#process">Process</a>
            <a href="#municipal">Municipal Programs</a>
            <a href="#about">Why Hi-Vis</a>
          </nav>
          <a className="button button--small button--orange" href="#quote">Request Assessment</a>
        </div>
      </header>

      <main>
        <section className="hero section-dark">
          <div className="hero-grid-pattern" aria-hidden="true" />
          <div className="container hero-grid">
            <div className="hero-copy">
              <p className="eyebrow">HYDRANT VISIBILITY • ASSET CONDITION • DOCUMENTATION</p>
              <h1>Visible.<br /><span>Documented.</span><br />Maintained.</h1>
              <p className="hero-lede">Specialized field services for municipalities, water utilities, fire districts, industrial facilities and private water-system owners that need hydrants easier to identify, document and maintain.</p>
              <div className="hero-actions">
                <a className="button button--orange" href="#quote">Request a No-Cost Route Assessment <ArrowIcon /></a>
                <a className="button button--ghost" href="#services">View Service Scope</a>
              </div>
              <div className="hero-meta">
                <span>Southeast Louisiana</span>
                <span>Mississippi Gulf Coast</span>
                <span>Municipal &amp; Utility Focus</span>
              </div>
            </div>

            <div className="hero-panel" aria-label="Hi-Vis operating model">
              <div className="hero-panel-topline">FIELD SERVICE OPERATING MODEL</div>
              <div className="hero-signal">VISIBLE</div>
              <div className="hero-signal hero-signal--outline">DOCUMENTED</div>
              <div className="hero-signal">MAINTAINED</div>
              <div className="hero-panel-footer">
                <span>ASSESS</span><b>→</b><span>DOCUMENT</span><b>→</b><span>RESTORE</span><b>→</b><span>VERIFY</span><b>→</b><span>MAINTAIN</span>
              </div>
            </div>
          </div>
        </section>

        <section className="trust-strip" aria-label="Positioning summary">
          <div className="container trust-strip-inner">
            <strong>FIELD-READY. PROCUREMENT-AWARE. SCOPE-DISCIPLINED.</strong>
            <span>Exterior visibility, condition, documentation, restoration and maintenance support.</span>
          </div>
        </section>

        <section className="section" id="services">
          <div className="container">
            <div className="section-heading split-heading">
              <div>
                <p className="eyebrow eyebrow--orange">CORE SERVICES</p>
                <h2>Hydrant field services built around asset visibility and accountable documentation.</h2>
              </div>
              <p>Hi-Vis focuses on the exterior, owner-authorized work that helps asset owners see current condition, document what exists and keep visible maintenance needs organized.</p>
            </div>
            <div className="service-grid">
              {services.map((service, index) => (
                <article className="service-card" key={service.title}>
                  <div className="service-card-top">
                    <div className="service-index">0{index + 1}</div>
                    <AssetIcon src={service.icon} alt="" />
                  </div>
                  <h3>{service.title}</h3>
                  <p>{service.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section section-process" id="process">
          <div className="container">
            <div className="section-heading">
              <p className="eyebrow eyebrow--orange">STANDARDIZED OPERATING SEQUENCE</p>
              <h2>Assess → Document → Restore → Verify → Maintain</h2>
            </div>
            <div className="process-list">
              {process.map(([number, title, icon, body]) => (
                <article className="process-row" key={title}>
                  <span className="process-number">{number}</span>
                  <div className="process-title"><AssetIcon src={icon} alt="" /><h3>{title}</h3></div>
                  <p>{body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section section-dark municipal" id="municipal">
          <div className="container municipal-grid">
            <div>
              <p className="eyebrow">MUNICIPAL &amp; UTILITY PROGRAMS</p>
              <h2>Start with a defined route. Build a defensible asset record.</h2>
              <p className="municipal-lede">For qualified asset owners, Hi-Vis can begin with a no-cost visual assessment of an owner-designated route so stakeholders can evaluate visible condition and documentation needs before discussing a larger scope.</p>
              <a className="button button--orange" href="#quote">Discuss a Pilot Route <ArrowIcon /></a>
            </div>
            <div className="pilot-card">
              <span className="pilot-kicker">PILOT OFFER</span>
              <strong className="pilot-number">25–50</strong>
              <span className="pilot-label">assets on an owner-designated route</span>
              <ul>
                <li>Asset location / ID</li>
                <li>Photographs</li>
                <li>Visible coating / marking condition</li>
                <li>Obstruction / access notes</li>
                <li>Identification visibility</li>
                <li>Recommended owner-approved action</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="section" id="about">
          <div className="container why-grid">
            <div className="why-intro">
              <p className="eyebrow eyebrow--orange">WHY HI-VIS</p>
              <h2>A focused field-service partner—not a broad claims contractor.</h2>
              <p>Our phase-one scope stays centered on visual assessment, exterior restoration, visibility, identification and documentation. Regulated technical scopes are outside the base service unless separately authorized and performed under applicable requirements.</p>
            </div>
            <div className="trust-list">
              {trustPoints.map((point) => (
                <div className="trust-item" key={point}><span aria-hidden="true">✓</span><p>{point}</p></div>
              ))}
            </div>
          </div>
        </section>

        <section className="quote-section" id="quote">
          <div className="container quote-heading-grid">
            <div>
              <p className="eyebrow eyebrow--orange">REQUEST AN ASSESSMENT</p>
              <h2>Have a route, district or facility with hydrants that need attention?</h2>
            </div>
            <p>Provide the owner, agency or facility name, general service area and approximate number of assets. We’ll use it to define the appropriate visual assessment or restoration scope.</p>
          </div>
          <div className="container">
            <QuoteForm />
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="container footer-grid">
          <div>
            <BrandLockup compact reversed />
            <p className="footer-tagline">VISIBLE. DOCUMENTED. MAINTAINED.</p>
          </div>
          <div className="footer-copy">
            <p>Hydrant visibility • exterior restoration • asset documentation • recurring maintenance</p>
            <p><a href={`mailto:${OPERATIONS_EMAIL}`}>{OPERATIONS_EMAIL}</a> • hivishydrant.com</p>
            <p>Initial service area: Southeast Louisiana + Mississippi Gulf Coast</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
